import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Installations from "./pages/Installations";
import InstallationDetail from "./pages/InstallationDetail";
import InstallationMaterials from "./pages/InstallationMaterials";
import DailyReports from "./pages/DailyReports";
import NewDailyReport from "./pages/NewDailyReport";
import DailyReportDetail from "./pages/DailyReportDetail";
import Users from "./pages/Users";
import EmailTemplates from "./pages/EmailTemplates";
import CalendarPage from "./pages/Calendar";
import MetricsPage from "./pages/Metrics";
import BulkExportPage from "./pages/BulkExport";
import EmailRecipients from "@/pages/EmailRecipients";
import Accounting from "@/pages/Accounting";
import IncomeView from "@/pages/IncomeView";
import ExpensesView from "@/pages/ExpensesView";
import { FinanceBudgetsView, FinanceProjectsMarginView } from "@/pages/FinancePlanningViews";
import FinanceLedgerView from "@/pages/FinanceLedgerView";
import LegacyTaxReconciliation from "@/pages/LegacyTaxReconciliation";
import FinanceImportsView from "@/pages/FinanceImportsView";
import FinanceTreasuryView from "@/pages/FinanceTreasuryView";
import { FinanceManagementView, FinanceSettingsView } from "@/pages/FinanceAdminViews";
import ProductCatalog from "@/pages/ProductCatalog";
import SupplierInvoices from "@/pages/SupplierInvoices";
import FinanceSuppliersView from "@/pages/FinanceSuppliersView";
import SupplierInvoiceDetail from "@/pages/SupplierInvoiceDetail";
import Customers from "@/pages/Customers";
import CommercialRequests from "@/pages/CommercialRequests";
import CommercialClients from "@/pages/CommercialClients";
import CommercialWizard from "@/pages/CommercialWizard";
import CustomerDetail from "@/pages/CustomerDetail";
import ContractDetail from "@/pages/ContractDetail";
import CustomerForm from "@/pages/CustomerForm";
import AdministrationDataMap from "@/pages/AdministrationDataMap.tsx";

function Router() {
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/login"} component={Home} />
      <Route path={"/dashboard"} component={Home} />
      <Route path={"/installations"} component={Installations} />
      <Route path={"/installations/:id"} component={InstallationDetail} />
      <Route path={"/installations/:id/materials"} component={InstallationMaterials} />
      <Route path={"/installations/:id/daily-reports/new"} component={NewDailyReport} />
      <Route path={"/daily-reports"} component={DailyReports} />
      <Route path={"/daily-reports/new"} component={NewDailyReport} />
      <Route path={"/daily-reports/:id"} component={DailyReportDetail} />
      <Route path={"/users"} component={Users} />
      <Route path={"/email-templates"} component={EmailTemplates} />
      <Route path={"/calendar"} component={CalendarPage} />
      <Route path={"/metrics"} component={MetricsPage} />
      <Route path={"/export-pdfs"} component={BulkExportPage} />
      <Route path={"/email-recipients"} component={EmailRecipients} />
      <Route path={"/accounting"} component={Accounting} />
      <Route path={"/accounting/income"} component={IncomeView} />
      <Route path={"/accounting/legacy-tax-reconciliation"} component={LegacyTaxReconciliation} />
      <Route path={"/accounting/expenses"} component={ExpensesView} />
      <Route path={"/accounting/suppliers"} component={FinanceSuppliersView} />
      <Route path={"/accounting/treasury"} component={FinanceTreasuryView} />
      <Route path={"/accounting/projects-margin"} component={FinanceProjectsMarginView} />
      <Route path={"/accounting/budgets"} component={FinanceBudgetsView} />
      <Route path={"/accounting/ledger"} component={FinanceLedgerView} />
      <Route path={"/accounting/imports"} component={FinanceImportsView} />
      <Route path={"/accounting/management"} component={FinanceManagementView} />
      <Route path={"/accounting/settings"} component={FinanceSettingsView} />
      {/* Legacy compat: /accounting/supplier-invoices redirects intent to ExpensesView;
          kept as standalone page so bookmarks and external links keep working. */}
      <Route path={"/accounting/supplier-invoices"} component={SupplierInvoices} />
      <Route path={"/accounting/supplier-invoices/:id"} component={SupplierInvoiceDetail} />
      <Route path={"/product-catalog"} component={ProductCatalog} />
      <Route path={"/commercial/requests"} component={CommercialRequests} />
      <Route path={"/commercial/clients"} component={CommercialClients} />
      <Route path={"/commercial/new"} component={CommercialWizard} />
      <Route path={"/customers"} component={Customers} />
      <Route path={"/customers/new"} component={CustomerForm} />
      <Route path={"/customers/:partnerId/edit"} component={CustomerForm} />
      <Route path={"/customers/:partnerId"} component={CustomerDetail} />
      <Route path={"/customers/:partnerId/contracts/:contractId"} component={ContractDetail} />
      <Route path={"/administration/data-map"} component={AdministrationDataMap} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
